import React, { useEffect, useMemo, useState } from "react";
import usmApi from "@/lib/usmApi";
import { useUsmAuth } from "@/contexts/UsmAuthContext";
import { PageHeader } from "../UsmLayout";
import HubTabs from "../hub/HubTabs";
import Can from "@/components/usm/Can";
import { toast } from "sonner";
import { Plus, Pencil, Power, PowerOff } from "lucide-react";

const EMPTY = {
  code: "", name: "", address_line1: "", address_line2: "", city: "", country: "Türkiye",
  phone: "", email: "", timezone: "Europe/Istanbul", currency_default: "TRY", is_active: true,
};

export default function BranchesList() {
  const { hasPerm } = useUsmAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { mode: 'create' | 'edit', data }

  const load = async () => {
    setLoading(true);
    try { setItems((await usmApi.get("/branches")).data || []); }
    catch (e) { toast.error("Şubeler yüklenemedi"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onSave = async (data) => {
    try {
      if (editing.mode === "create") await usmApi.post("/branches", data);
      else await usmApi.patch(`/branches/${editing.data.id}`, data);
      toast.success("Kaydedildi");
      setEditing(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kayıt başarısız"); }
  };

  const toggleActive = async (b) => {
    const action = b.is_active ? "deactivate" : "activate";
    try {
      await usmApi.post(`/branches/${b.id}/${action}`);
      toast.success(b.is_active ? "Şube devre dışı bırakıldı" : "Şube aktifleştirildi");
      await load();
    } catch (e) { toast.error("İşlem başarısız"); }
  };

  return (
    <div data-testid="usm-branches">
      <HubTabs section="branches" />
      <PageHeader
        title="Şubeler"
        breadcrumbs={[{ label: "Panel", to: "/usm" }, { label: "Şubeler" }]}
        actions={
          <Can perm="branches.manage">
            <button
              data-testid="branches-new-btn"
              onClick={() => setEditing({ mode: "create", data: EMPTY })}
              className="bg-[#0A0A0A] text-white text-xs uppercase tracking-[0.2em] px-5 py-3 inline-flex items-center gap-2"
            >
              <Plus size={14} /> Yeni Şube
            </button>
          </Can>
        }
      />

      <div className="bg-white border border-[#E7E5E4] overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead className="bg-[#FAFAF9] text-[11px] uppercase tracking-[0.15em] text-[#71717A]">
            <tr>
              <th className="text-left px-4 py-3">Kod</th>
              <th className="text-left px-4 py-3">Şube</th>
              <th className="text-left px-4 py-3">Şehir</th>
              <th className="text-left px-4 py-3">Telefon</th>
              <th className="text-left px-4 py-3">Durum</th>
              <th className="text-right px-4 py-3 w-32">İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-[#71717A]">Yükleniyor...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-[#71717A]">Henüz şube yok. {hasPerm("branches.manage") && "Sağ üstten ekleyin."}</td></tr>
            ) : items.map((b) => (
              <tr key={b.id} data-testid={`branch-row-${b.code}`} className="border-t border-[#F5F5F4]">
                <td className="px-4 py-3 font-mono text-[12px]">{b.code}</td>
                <td className="px-4 py-3">{b.name}</td>
                <td className="px-4 py-3">{b.city || "—"}</td>
                <td className="px-4 py-3">{b.phone || "—"}</td>
                <td className="px-4 py-3">
                  {b.is_active
                    ? <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.15em] text-emerald-700">● Aktif</span>
                    : <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.15em] text-[#71717A]">● Pasif</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  <Can perm="branches.manage">
                    <button onClick={() => setEditing({ mode: "edit", data: b })} className="p-2 hover:bg-[#FAFAF9]" title="Düzenle"><Pencil size={14} /></button>
                    <button onClick={() => toggleActive(b)} className="p-2 hover:bg-[#FAFAF9]" title={b.is_active ? "Devre dışı bırak" : "Aktifleştir"}>
                      {b.is_active ? <PowerOff size={14} /> : <Power size={14} />}
                    </button>
                  </Can>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <BranchEditor data={editing.data} mode={editing.mode} onClose={() => setEditing(null)} onSave={onSave} />}
    </div>
  );
}

function BranchEditor({ data, mode, onClose, onSave }) {
  const [form, setForm] = useState(data);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const valid = useMemo(() => form.code?.trim().length >= 2 && form.name?.trim().length >= 1, [form]);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div data-testid="branch-editor" className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[#E7E5E4] flex items-center justify-between">
          <h2 className="font-heading text-xl">{mode === "create" ? "Yeni Şube" : "Şubeyi Düzenle"}</h2>
          <button onClick={onClose} className="text-[#71717A]">✕</button>
        </div>
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Kod *" testid="branch-code"><input data-testid="branch-code-input" value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())} className={inputCls} /></Field>
          <Field label="Şube Adı *" testid="branch-name"><input data-testid="branch-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} /></Field>
          <Field label="Adres 1"><input value={form.address_line1 || ""} onChange={(e) => set("address_line1", e.target.value)} className={inputCls} /></Field>
          <Field label="Adres 2"><input value={form.address_line2 || ""} onChange={(e) => set("address_line2", e.target.value)} className={inputCls} /></Field>
          <Field label="Şehir"><input value={form.city || ""} onChange={(e) => set("city", e.target.value)} className={inputCls} /></Field>
          <Field label="Ülke"><input value={form.country || ""} onChange={(e) => set("country", e.target.value)} className={inputCls} /></Field>
          <Field label="Telefon"><input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} className={inputCls} /></Field>
          <Field label="E-posta"><input type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} className={inputCls} /></Field>
        </div>
        <div className="px-6 py-4 border-t border-[#E7E5E4] flex justify-end gap-3">
          <button onClick={onClose} className="text-xs uppercase tracking-[0.2em] px-5 py-3 hover:bg-[#FAFAF9]">İptal</button>
          <button data-testid="branch-save-btn" disabled={!valid} onClick={() => onSave(form)} className="bg-[#0A0A0A] disabled:opacity-40 text-white text-xs uppercase tracking-[0.2em] px-6 py-3">Kaydet</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <label className="text-[11px] uppercase tracking-[0.15em] text-[#71717A] block mb-1.5">{label}</label>
      {children}
    </div>
  );
}

const inputCls = "w-full px-3 py-2.5 border border-[#E7E5E4] focus:border-[#C8A96A] outline-none text-sm";
